import React from 'react';
import { Button, Grid, Hidden, IconButton, InputAdornment, TextField, } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import logo from '../../logo126.svg';
import ClearIcon from '@material-ui/icons/Clear';
import './sign-up.scss'
import { useHistory, useLocation } from 'react-router';
import * as authActions from '../../store/auth/authSlice';
import { connect } from 'react-redux';
import { useFormik } from 'formik';
import { isEmail } from 'validator';
import { authCodeLength, completedRoutes } from '../../common/constants';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { get } from 'lodash';
import TermsOfUse from '../common/TermsOfUse';

const phoneNumberRegex = /^(\+7|7|8)[\s-]?\(?[489][0-9]{2}\)?[\s-]?[0-9]{3}[\s-]?[0-9]{2}[\s-]?[0-9]{2}$/;

function SignUp(props) {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const currentUrl = get(location, 'state.currentUrl', completedRoutes.switchUserType);
  const { hasVerificationCode } = props;

  const formik = useFormik({
    initialValues: {
      phoneOrEmail: '',
      verificationCode: '',
    },
    validate(values) {
      const { phoneOrEmail } = values;
      const errors = {};
      const isValueEmail = isEmail(phoneOrEmail);
      const isValuePhoneNumber = phoneNumberRegex.test(phoneOrEmail);
      if (!isValueEmail && !isValuePhoneNumber) {
        errors.phoneOrEmail = t('registrationForm.error');
      }

      return errors;
    },
    onSubmit(values, actions) {
      const { phoneOrEmail, verificationCode } = values;
      const isValueEmail = isEmail(phoneOrEmail);
      const isValuePhoneNumber = phoneNumberRegex.test(phoneOrEmail);
      const phone = isValuePhoneNumber ? parsePhoneNumberFromString(phoneOrEmail, 'RU').number : '';
      const email = isValueEmail ? phoneOrEmail : '';

      !hasVerificationCode ?
        props.register(phone, email).then(() => {
          formik.setSubmitting(false);
        }) :
        props.sendCode(phone, email, verificationCode)
          .then(() => {
            history.replace(currentUrl, { ...location.state });
          })
          .catch((error) => {
            if (error.status === 403) {
              actions.setFieldError('verificationCode', t('registrationForm.verificationCodeError'));
            }
          })
          .finally(() => {
            formik.setSubmitting(false);
          });
    },
  });

  const handleChangeRegisterData = () => {
    props.resetRegistration();
    formik.resetForm();
  };

  const { phoneOrEmail, verificationCode } = formik.values;
  const { handleChange, handleSubmit, errors, handleBlur } = formik;

  const handleChangeAuthCode = (e) => {
    const { name, value } = e.target;
    formik.setFieldValue(name, value);
    if (value.length === authCodeLength) {
      handleSubmit();
    }
  };

  const isFetching = formik.isSubmitting && !formik.isValidating;
  const isSubmitButtonDisabled = isFetching
    || !!errors.phoneOrEmail
    || phoneOrEmail === '';

  const { pageType, withLogo } = props;

  return (
    <div className={`sign-cnt ${pageType ? '' : 'mt'}`}>
      {!pageType || withLogo
        ? <img src={logo} alt="Like&amp;Go" className="logo" height="126" width="124" />
        : (
          <div className="page-type-info">
            <h2>{t('registrationForm.pageType.h')}</h2>
            <p>{t('registrationForm.pageType.p')}</p>
          </div>
        )
      }
      <form className="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}
              direction="column"
              justify="space-between"
        >
          <Grid item xs={12}>
            <TextField
              autoComplete="femail"
              fullWidth
              name="phoneOrEmail"
              label={t('registrationForm.value')}
              value={phoneOrEmail}
              helperText={!!props.registerError && t('registrationForm.error')}
              error={!!errors.phoneOrEmail || !!props.registerError}
              onChange={handleChange}
              onBlur={handleBlur}
              className="text-input"
              InputProps={{
                endAdornment: (
                  <>
                    {phoneOrEmail !== '' && (
                      <InputAdornment position="end">
                        <IconButton
                          className="clear-btn"
                          aria-label="clear value"
                          onClick={handleChangeRegisterData}
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    )}
                  </>
                )
              }}
            />
          </Grid>
          {!hasVerificationCode ? null :
            <Grid item xs={12}>
              <TextField
                fullWidth
                className="text-input"
                id="verificationCode"
                label={t('registrationForm.verificationCode')}
                name="verificationCode"
                type="number"
                helperText={
                  !!errors.verificationCode && t('registrationForm.verificationCodeError')
                }
                error={!!errors.verificationCode}
                value={verificationCode}
                onChange={handleChangeAuthCode}
              />
            </Grid>
          }
        </Grid>
        <Grid container
              direction="column"
              justify="space-between"
        >
          <Button
            fullWidth
            type="submit"
            variant="contained"
            className="submit"
            color="secondary"
            disabled={isSubmitButtonDisabled}
          >
            {t('registrationForm.submitButton')}
          </Button>
        </Grid>
      </form>
      <Hidden mdUp>
        <Grid item xs={12}>
          <TermsOfUse to={completedRoutes.userRules} />
        </Grid>
      </Hidden>
    </div>
  );
}

const mapStateToProps = state => ({
  authFetchingState: state.auth.fetchingState,
  registerError: state.auth.registerError,
  verificationError: state.auth.verificationError,
  isAdmin: state.auth.isAdmin,
  hasVerificationCode: state.auth.hasVerificationCode,
  userName: state.auth.userName,
});

const actionCreators = {
  register: authActions.register,
  sendCode: authActions.sendCode,
  resetRegistration: authActions.resetRegistration,
};

export default connect(mapStateToProps, actionCreators)(SignUp);
